import { useEffect, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { API } from "../../config";

import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";

import { Autoplay, Navigation, Pagination } from "swiper/modules";

import bootomwave from "/images/headesliderTop.webp";
import bootomMobilewave from "/images/MobGroup.webp";

interface Slider {
  slider_id: number;
  image_url: string | null;
  heading: string | null;
  description: string | null;
  button_text: string | null;
  button_link: string | null;
  stat1_value: string | null;
  stat1_text: string | null;
  stat2_value: string | null;
  stat2_text: string | null;
  stat3_value: string | null;
  stat3_text: string | null;
  display_order: number;
}

export default function HeroSlider() {
  const [sliders, setSliders] = useState<Slider[]>([]);

  useEffect(() => {
    fetch(`${API}/home_sliders`)
      .then((res) => res.json())
      .then((data) => {
        const active = (data.sliders as Slider[]).filter(
          (s: any) => s.record_status_id === 1
        );
        active.sort((a, b) => a.display_order - b.display_order);
        setSliders(active);
      })
      .catch((err) => console.error("Failed to load sliders:", err));
  }, []);

  // Reserve the hero's height even before the API responds, so the rest of the
  // page doesn't jump when slides arrive (this was the main CLS / layout-shift
  // cause: the component previously rendered nothing until data loaded).
  if (sliders.length === 0) {
    return <div className="md:h-[620px] h-[400px] w-full bg-gray-100" aria-hidden="true" />;
  }

  return (
    <div>
      <Swiper
        modules={[Autoplay, Pagination, Navigation]}
        autoplay={{ delay: 4000, disableOnInteraction: false }}
        pagination={{ el: ".course-pagination", clickable: true }}
        loop
        grabCursor
        navigation={{
          nextEl: ".hero-next",
          prevEl: ".hero-prev",
        }}
        className="md:h-[620px] h-[400px] heroSwiper"
      >
        {sliders.map((slider, idx) => (
          <SwiperSlide key={slider.slider_id}>
            <div className="swiper-slide relative ">
              {slider.image_url && (
                <img
                  src={slider.image_url}
                  className="w-full md:h-[620px] h-[400px] md:h-auto object-cover"
                  alt={slider.heading ?? "Phonics Power"}
                  width={1600}
                  height={620}
                  // First slide is the LCP element: load it eagerly and at high
                  // priority. object-cover keeps it undistorted despite the ratio.
                  fetchPriority={idx === 0 ? "high" : "auto"}
                  loading={idx === 0 ? "eager" : "lazy"}
                  decoding="async"
                />
              )}

              {/* Overlay */}
              {/* <!-- Overlay --> */}
              <div className="absolute bottom-0 left-0 w-full">
                {/* Bottom Shape Image (decorative) */}
                <img
                  src={bootomwave}
                  className="hidden md:block w-full h-[164px] object-cover"
                  alt=""
                  aria-hidden="true"
                  width={1160}
                  height={175}
                  loading="lazy"
                  decoding="async"
                />
                {/* Mobile Image (decorative) */}
                <img
                  src={bootomMobilewave}
                  className="block md:hidden w-full"
                  alt=""
                  aria-hidden="true"
                  width={402}
                  height={135}
                  loading="lazy"
                  decoding="async"
                />
                {slider.heading && (
                  <div className="absolute bottom-85 flex md:hidden px-8 w-full">
                    <h2 className="text-white text-[20px] font-semibold">
                      {slider.heading}
                    </h2>
                  </div>
                )}
                {/* Stats + Heading */}
                <div className="pl-custom absolute bottom-6 flex gap-20 justify-between left-0 px-8 text-center w-full">
                   {slider.heading && (
                    <div className="hidden justify-end md:block bottom-font">
                      <h2 className="text-white text-3xl md:text-5xl font-semibold">
                        {slider.heading}
                      </h2>
                    </div>
                  )}

                  {slider.stat1_value && (
                    <div className="pt-6 md:px-8 w-64">
                      <p className="highlight-text">{slider.stat1_value}</p>
                      <p className="font-semibold heroBtmText">
                        {slider.stat1_text}
                      </p>
                    </div>
                  )}

                  {slider.stat2_value && (
                    <div className="pt-6 md:px-8 w-64">
                      <p className="highlight-text">{slider.stat2_value}</p>
                      <p className="heroBtmText font-semibold">
                        {slider.stat2_text}
                      </p>
                    </div>
                  )}

                  {slider.stat3_value && (
                    <div className="pt-6 md:px-5 w-64">
                      <p className="highlight-text">{slider.stat3_value}</p>
                      <p className="font-semibold heroBtmText">
                        {slider.stat3_text}
                      </p>
                    </div>
                  )}

                 
                </div>
              </div>

              {/* Button */}
              {slider.button_text && slider.button_link && (
                <div className="absolute bottom-36 left-8">
                  <a
                    href={slider.button_link}
                    className="bg-red-600 text-white px-6 py-3 rounded font-semibold hover:bg-red-700 transition"
                  >
                    {slider.button_text}
                  </a>
                </div>
              )}
            </div>
          </SwiperSlide>
        ))}
      </Swiper>
      <div className="hero-prev hidden md:flex" aria-hidden="true">
        <b>❮</b>
      </div>

      <div className="hero-next hidden md:flex" aria-hidden="true">
        <b>❯</b>
      </div>
    </div>
  );
}
